import { createTheme } from "@mui/material/styles";
import { rawColor, colors } from "../constants/colors";

export const sageTheme = createTheme({
  name: "sage",
  palette: {
    mode: "light",
    primary: { main: rawColor.sage.main },
    secondary: { main: rawColor.sage.secondary },
    background: {
      default: rawColor.sage.background,
      paper: rawColor.sage.paper,
    },
    text: {
      primary: rawColor.sage.text.primary,
      secondary: rawColor.sage.text.secondary,
    },
    action: {
      hover: rawColor.sage.sidebar.hover,
    },
    error: { main: rawColor.status.error.light },
    warning: { main: rawColor.status.warning },
    success: { main: rawColor.status.active.green },
    divider: rawColor.sage.divider,
  },
  sidebar: {
    base: colors.sidebar.sage.base,
    hover: colors.sidebar.sage.hover,
    selected: colors.sidebar.sage.selected,
  },
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.sidebar.base,
          borderRight: `1px solid ${theme.palette.divider}`,
          "& .MuiListItemIcon-root": {
            color: rawColor.sage.text.secondary,
          },
          "& .MuiListItemText-root": {
            color: rawColor.sage.text.primary,
          },
          "& .MuiListItemButton-root.Mui-selected": {
            backgroundColor: theme.sidebar.selected,
            "& .MuiListItemIcon-root": {
              color: rawColor.sage.main,
            },
            "& .MuiListItemText-root": {
              color: rawColor.sage.main,
            },
          },
          "& .MuiListItemButton-root:hover": {
            backgroundColor: theme.sidebar.hover,
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          backgroundColor: colors.table.sage.cell,
          color: rawColor.sage.text.primary,
        },
        head: {
          backgroundColor: colors.table.sage.header,
          color: colors.table.sage.headerText,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: rawColor.sage.paper,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h5: {
          color: rawColor.sage.text.primary,
        },
        h6: {
          color: rawColor.sage.text.primary,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: rawColor.sage.text.primary,
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: rawColor.sage.text.secondary,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          "& .MuiSvgIcon-root": {
            color: "currentColor",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: rawColor.sage.text.secondary,
          "& .MuiInputBase-input": {
            color: rawColor.sage.text.secondary,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: rawColor.sage.text.secondary,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.sage.divider,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.sage.text.secondary,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.sage.main,
          },
          "& .MuiOutlinedInput-input": {
            color: rawColor.sage.text.secondary,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: rawColor.sage.text.secondary,
          "&.Mui-focused": {
            color: rawColor.sage.main,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: rawColor.sage.text.secondary,
        },
        select: {
          color: rawColor.sage.text.secondary,
        },
        selectLabel: {
          color: rawColor.sage.text.secondary,
        },
        selectIcon: {
          color: rawColor.sage.text.secondary,
        },
        displayedRows: {
          color: rawColor.sage.text.secondary,
        },
        actions: {
          color: rawColor.sage.text.secondary,
          "& .MuiSvgIcon-root": {
            color: rawColor.sage.text.secondary,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiAutocomplete-option": {
            color: rawColor.sage.text.secondary,
            "&:hover": {
              backgroundColor: rawColor.sage.sidebar.hover,
            },
            '&[aria-selected="true"]': {
              backgroundColor: rawColor.sage.sidebar.selected,
              color: rawColor.sage.main,
              "&:hover": {
                backgroundColor: rawColor.sage.sidebar.selected,
              },
            },
          },
          "& .MuiAutocomplete-listbox": {
            backgroundColor: rawColor.sage.paper,
            "& .MuiAutocomplete-option": {
              padding: "8px 16px",
            },
          },
          "& .MuiAutocomplete-option .MuiTypography-root": {
            color: "inherit",
          },
          "& .MuiAutocomplete-option .MuiListItemText-root": {
            color: "inherit",
          },
        },
        paper: {
          backgroundColor: rawColor.sage.paper,
          "& .MuiAutocomplete-option": {
            color: rawColor.sage.text.secondary,
          },
        },
        listbox: {
          "& .MuiAutocomplete-option": {
            color: rawColor.sage.text.secondary,
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        ".select__control": {
          backgroundColor: `${rawColor.sage.paper} !important`,
          borderColor: `${rawColor.sage.divider} !important`,
          "&:hover": {
            borderColor: `${rawColor.sage.text.secondary} !important`,
          },
          "&--is-focused": {
            borderColor: `${rawColor.sage.main} !important`,
            boxShadow: `0 0 0 1px ${rawColor.sage.main} !important`,
          },
        },
        ".select__menu": {
          backgroundColor: `${rawColor.sage.paper} !important`,
          border: `1px solid ${rawColor.sage.divider} !important`,
        },
        ".select__option": {
          backgroundColor: `${rawColor.sage.paper} !important`,
          color: `${rawColor.sage.text.secondary} !important`,
          "&:hover": {
            backgroundColor: `${rawColor.sage.sidebar.hover} !important`,
          },
          "&--is-focused": {
            backgroundColor: `${rawColor.sage.sidebar.hover} !important`,
          },
          "&--is-selected": {
            backgroundColor: `${rawColor.sage.sidebar.selected} !important`,
            color: `${rawColor.sage.main} !important`,
            "&:hover": {
              backgroundColor: `${rawColor.sage.sidebar.selected} !important`,
            },
          },
        },
        ".select__multi-value": {
          backgroundColor: `${rawColor.sage.sidebar.hover} !important`,
          "&__label": {
            color: `${rawColor.sage.text.secondary} !important`,
          },
          "&__remove": {
            color: `${rawColor.sage.text.secondary} !important`,
            "&:hover": {
              backgroundColor: `${rawColor.sage.sidebar.selected} !important`,
              color: `${rawColor.sage.main} !important`,
            },
          },
        },
        ".select__placeholder": {
          color: `${rawColor.sage.text.secondary} !important`,
        },
        ".select__single-value": {
          color: `${rawColor.sage.text.secondary} !important`,
        },
        ".select__input": {
          color: `${rawColor.sage.text.secondary} !important`,
        },
        ".select__indicator": {
          color: `${rawColor.sage.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.sage.main} !important`,
          },
        },
        ".select__indicator-separator": {
          backgroundColor: `${rawColor.sage.divider} !important`,
        },
        ".select__clear-indicator": {
          color: `${rawColor.sage.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.sage.main} !important`,
          },
        },
        ".select__dropdown-indicator": {
          color: `${rawColor.sage.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.sage.main} !important`,
          },
        },
      },
    },
  },
});
